/**
 * Rax Approval Queue Component
 *
 * Displays pending approval requests from autonomous agents.
 * Allows users to approve or deny queued actions with real-time polling.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type ApprovalQueueEntry } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-approval-queue")
export class RaxApprovalQueue extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }

      .queue-container {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }

      .queue-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }

      .queue-header h2 {
        margin: 0;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .queue-header h2::before {
        content: "\\2714";
        font-size: var(--font-xl);
        color: var(--rax-yellow);
      }

      .pending-count {
        background: var(--rax-yellow);
        color: var(--rax-black);
        font-size: var(--font-xs);
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 12px;
        margin-left: var(--space-2);
      }

      .queue-actions {
        display: flex;
        gap: var(--space-2);
      }

      .refresh-btn {
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        cursor: pointer;
        color: var(--text-tertiary);
        font-size: var(--font-sm);
        transition: all var(--transition-fast);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .refresh-btn:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }

      .refresh-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .refresh-btn.spinning::before {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .queue-list {
        display: flex;
        flex-direction: column;
      }

      .approval-card {
        display: flex;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        transition: background var(--transition-fast);
      }

      .approval-card:last-child {
        border-bottom: none;
      }

      .approval-card:hover {
        background: var(--bg-hover);
      }

      .approval-card.resolving {
        opacity: 0.6;
        pointer-events: none;
      }

      .approval-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md);
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.2), rgba(255, 208, 0, 0.1));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-xl);
        flex-shrink: 0;
        color: var(--rax-yellow);
      }

      .approval-icon.check {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
        color: #3b82f6;
      }

      .approval-content {
        flex: 1;
        min-width: 0;
      }

      .approval-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-1);
        flex-wrap: wrap;
      }

      .approval-task {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
      }

      .approval-class-badge {
        font-size: var(--font-xs);
        font-weight: 600;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .approval-class-badge.do {
        background: rgba(239, 68, 68, 0.2);
        color: #ef4444;
      }

      .approval-class-badge.check {
        background: rgba(59, 130, 246, 0.2);
        color: #3b82f6;
      }

      .approval-tool {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        font-family: var(--font-mono);
        background: var(--bg-subtle);
        padding: 1px 4px;
        border-radius: var(--radius-sm);
      }

      .approval-reason {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin: 0 0 var(--space-2);
        line-height: 1.5;
      }

      .approval-meta {
        display: flex;
        gap: var(--space-4);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      .approval-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .approval-expires {
        color: var(--warning);
      }

      .approval-expires.urgent {
        color: var(--error);
        font-weight: 600;
      }

      .approval-actions {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        flex-shrink: 0;
      }

      .approve-btn,
      .deny-btn {
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
        min-width: 80px;
        text-align: center;
      }

      .approve-btn {
        background: var(--rax-yellow);
        border: none;
        color: var(--rax-black);
      }

      .approve-btn:hover {
        background: var(--rax-yellow-hover);
        transform: translateY(-1px);
      }

      .deny-btn {
        background: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-tertiary);
      }

      .deny-btn:hover {
        border-color: var(--error);
        color: var(--error);
      }

      .approve-btn:disabled,
      .deny-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      .empty-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }

      .empty-state-icon {
        font-size: 3rem;
        margin-bottom: var(--space-4);
        opacity: 0.5;
      }

      .empty-state h3 {
        margin: 0 0 var(--space-2);
        font-size: var(--font-lg);
        color: var(--text-secondary);
      }

      .empty-state p {
        margin: 0;
        font-size: var(--font-sm);
      }

      .loading-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }

      .error-state {
        padding: var(--space-4);
        background: rgba(239, 68, 68, 0.14);
        border: 1px solid rgba(239, 68, 68, 0.55);
        border-radius: var(--radius-md);
        color: #fca5a5;
        margin: var(--space-4);
      }

      .toast {
        position: fixed;
        bottom: var(--space-4);
        right: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--rax-yellow);
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-4);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: var(--space-3);
        animation: slideIn 0.3s ease-out;
      }

      .toast.hidden {
        display: none;
      }

      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .toast-icon {
        font-size: var(--font-xl);
      }

      .toast-content {
        flex: 1;
      }

      .toast-title {
        font-weight: 600;
        color: var(--text-primary);
        font-size: var(--font-sm);
      }

      .toast-message {
        color: var(--text-secondary);
        font-size: var(--font-xs);
      }

      .toast-close {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        font-size: var(--font-lg);
        padding: 0;
        line-height: 1;
      }

      .toast-close:hover {
        color: var(--text-primary);
      }

      @media (max-width: 640px) {
        .approval-card {
          flex-direction: column;
          gap: var(--space-3);
        }

        .approval-actions {
          flex-direction: row;
          width: 100%;
        }

        .approve-btn,
        .deny-btn {
          flex: 1;
        }
      }
    `,
  ];

  @property({ type: Number })
  pollIntervalMs = 5000;

  @property({ type: Boolean })
  showToasts = true;

  @state()
  private approvals: ApprovalQueueEntry[] = [];

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private resolvingIds: Set<string> = new Set();

  @state()
  private previousCount = 0;

  @state()
  private toastMessage: { title: string; message: string } | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this.loadApprovals();
    this.startPolling();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPolling();
  }

  private startPolling() {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => {
      this.pollApprovals();
    }, this.pollIntervalMs);
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async loadApprovals() {
    this.loading = true;
    this.error = null;

    try {
      const response = await raxApi.listApprovals();
      if (response.success && response.data) {
        this.approvals = response.data;
        this.previousCount = response.data.length;
      } else {
        this.error = response.error || "Failed to load approvals";
      }
    } catch (err) {
      console.error("[ApprovalQueue] Error loading approvals:", err);
      this.error = err instanceof Error ? err.message : "Failed to load approvals";
    } finally {
      this.loading = false;
    }
  }

  private async pollApprovals() {
    try {
      const response = await raxApi.listApprovals();
      if (response.success && response.data) {
        const newCount = response.data.length;

        // Check for new approvals
        if (newCount > this.previousCount && this.showToasts) {
          const newApprovals = response.data.filter(
            (a) => !this.approvals.find((existing) => existing.id === a.id),
          );
          if (newApprovals.length > 0) {
            this.showNewApprovalToast(newApprovals[0]);
          }
        }

        this.approvals = response.data;
        this.previousCount = newCount;
        this.error = null;
      }
    } catch (err) {
      console.error("[ApprovalQueue] Poll error:", err);
    }
  }

  private showNewApprovalToast(approval: ApprovalQueueEntry) {
    this.toastMessage = {
      title: "New approval request",
      message: `${approval.taskKey}: ${approval.reason.slice(0, 60)}${approval.reason.length > 60 ? "..." : ""}`,
    };

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.toastMessage = null;
    }, 5000);

    this.dispatchEvent(
      new CustomEvent("new-approval", {
        detail: { approval },
        bubbles: true,
      }),
    );
  }

  private dismissToast() {
    this.toastMessage = null;
  }

  override render() {
    return html`
      <div class="queue-container">
        <div class="queue-header">
          <h2>
            Approval Queue
            ${
              this.approvals.length > 0
                ? html`
              <span class="pending-count">${this.approvals.length}</span>
            `
                : nothing
            }
          </h2>
          <div class="queue-actions">
            <button
              class="refresh-btn ${this.loading ? "spinning" : ""}"
              ?disabled=${this.loading}
              @click=${this.loadApprovals}
              title="Refresh"
            >
              Refresh
            </button>
          </div>
        </div>

        ${
          this.loading && this.approvals.length === 0
            ? html`
          <div class="loading-state">Loading pending approvals...</div>
        `
            : this.error
              ? html`
          <div class="error-state">${this.error}</div>
        `
              : this.approvals.length === 0
                ? html`
          <div class="empty-state">
            <div class="empty-state-icon">&#9989;</div>
            <h3>No pending approvals</h3>
            <p>When your agents need approval for actions, they will appear here.</p>
          </div>
        `
                : html`
          <div class="queue-list">
            ${this.approvals.map((approval) => this.renderApprovalCard(approval))}
          </div>
        `
        }
      </div>

      ${this.renderToast()}
    `;
  }

  private renderApprovalCard(approval: ApprovalQueueEntry) {
    const isResolving = this.resolvingIds.has(approval.id);
    const expiresAt = new Date(approval.expiresAt);
    const now = new Date();
    const remainingMs = expiresAt.getTime() - now.getTime();
    const remainingMins = Math.max(0, Math.floor(remainingMs / 60000));
    const isUrgent = remainingMins < 2;

    return html`
      <div class="approval-card ${isResolving ? "resolving" : ""}">
        <div class="approval-icon ${approval.actionClass}">
          ${approval.actionClass === "do" ? "\\270D" : "\\1F50D"}
        </div>
        <div class="approval-content">
          <div class="approval-header">
            <span class="approval-task">${approval.taskKey}</span>
            <span class="approval-class-badge ${approval.actionClass}">
              ${approval.actionClass}
            </span>
            ${
              approval.toolName
                ? html`
              <span class="approval-tool">${approval.toolName}</span>
            `
                : nothing
            }
          </div>
          <p class="approval-reason">${approval.reason}</p>
          <div class="approval-meta">
            <span class="approval-meta-item">
              Created ${this.formatTime(approval.createdAt)}
            </span>
            <span class="approval-meta-item approval-expires ${isUrgent ? "urgent" : ""}">
              ${remainingMins > 0 ? `Expires in ${remainingMins}m` : "Expiring soon"}
            </span>
          </div>
        </div>
        <div class="approval-actions">
          <button
            class="approve-btn"
            ?disabled=${isResolving}
            @click=${() => this.resolveApproval(approval.id, "approve")}
          >
            Approve
          </button>
          <button
            class="deny-btn"
            ?disabled=${isResolving}
            @click=${() => this.resolveApproval(approval.id, "deny")}
          >
            Deny
          </button>
        </div>
      </div>
    `;
  }

  private renderToast() {
    if (!this.toastMessage) {
      return html`<div class="toast hidden"></div>`;
    }

    return html`
      <div class="toast">
        <span class="toast-icon">&#128276;</span>
        <div class="toast-content">
          <div class="toast-title">${this.toastMessage.title}</div>
          <div class="toast-message">${this.toastMessage.message}</div>
        </div>
        <button class="toast-close" @click=${this.dismissToast}>&#215;</button>
      </div>
    `;
  }

  private formatTime(isoString: string): string {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) {
        return "just now";
      } else if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else {
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) {
          return `${diffHours}h ago`;
        }
        return date.toLocaleDateString();
      }
    } catch {
      return isoString;
    }
  }

  private async resolveApproval(id: string, decision: "approve" | "deny") {
    this.resolvingIds = new Set([...this.resolvingIds, id]);

    try {
      const response = await raxApi.resolveApproval(id, decision);
      if (response.success) {
        // Remove from list
        this.approvals = this.approvals.filter((a) => a.id !== id);
        this.previousCount = this.approvals.length;

        this.dispatchEvent(
          new CustomEvent("approval-resolved", {
            detail: { approval: response.data, decision },
            bubbles: true,
          }),
        );
      } else {
        console.error("[ApprovalQueue] Failed to resolve approval:", response.error);
        this.error = response.error || "Failed to resolve approval";
      }
    } catch (err) {
      console.error("[ApprovalQueue] Error resolving approval:", err);
      this.error = err instanceof Error ? err.message : "Failed to resolve approval";
    } finally {
      const newResolvingIds = new Set(this.resolvingIds);
      newResolvingIds.delete(id);
      this.resolvingIds = newResolvingIds;
    }
  }

  /** Public method to refresh the approvals list */
  public async refresh() {
    await this.loadApprovals();
  }

  /** Public method to get current pending count */
  public getPendingCount(): number {
    return this.approvals.length;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-approval-queue": RaxApprovalQueue;
  }
}
